import Layout from "@/components/layout/Layout";
import PageHero from "@/components/shared/PageHero";
import ServerPricingTable from "@/components/shared/ServerPricingTable";
import FAQSection from "@/components/home/FAQSection";
import PartnersSection from "@/components/home/PartnersSection";
import SEOHead from "@/components/seo/SEOHead";
import SchemaMarkup from "@/components/seo/SchemaMarkup";
import { Server, Cpu, HardDrive, Shield, Zap, Globe } from "lucide-react";

const packages = [
  {
    name: "E5-2680v4",
    processor: "Intel Xeon E5-2680v4",
    ram: "64 GB",
    disk: "1 TB NVMe SSD",
    bandwidth: "Unlimited",
    price: "26,499",
    period: "/month",
    orderLink: "https://my.webhostbuybd.com/store/bdix-dedicated-server/e5-2680v4",
  },
  {
    name: "E5-2695v4",
    processor: "Intel Xeon E5-2695v4",
    ram: "128 GB",
    disk: "2 TB NVMe SSD",
    bandwidth: "Unlimited",
    price: "34,499",
    period: "/month",
    orderLink: "https://my.webhostbuybd.com/store/bdix-dedicated-server/e5-2695v4",
  },
];

const features = [
  { icon: Server, title: "Bare Metal", description: "Dedicated BDIX hardware" },
  { icon: Cpu, title: "Intel Xeon", description: "Enterprise processors" },
  { icon: HardDrive, title: "NVMe RAID", description: "Redundant storage" },
  { icon: Zap, title: "10 Gbps", description: "High-speed BDIX network" },
  { icon: Shield, title: "DDoS", description: "Protection included" },
  { icon: Globe, title: "99.99% SLA", description: "Guaranteed uptime" },
];

const dedicatedFaqs = [
  {
    question: "What is BDIX hosting and why is it important?",
    answer: "BDIX (Bangladesh Internet Exchange) hosting provides ultra-fast connectivity within Bangladesh by routing traffic through local exchange points. This results in significantly lower latency and faster page load times for visitors within Bangladesh, making it ideal for local businesses and websites targeting Bangladeshi audiences.",
  },
  {
    question: "Do you offer free website migration?",
    answer: "Yes! We offer completely free website migration for all our hosting plans. Our expert team will migrate your website, databases, emails, and all files from your current host to our servers with zero downtime.",
  },
  {
    question: "What control panel do you use?",
    answer: "We use cPanel, the industry's most popular and user-friendly control panel. It provides an intuitive interface for managing your website, emails, databases, files, and more. We also offer one-click installers for WordPress, Joomla, and 400+ other applications.",
  },
  {
    question: "What is your uptime guarantee?",
    answer: "We guarantee 99.99% uptime for all our hosting services, backed by our Service Level Agreement (SLA). Our state-of-the-art datacenter infrastructure, redundant power systems, and 24/7 monitoring ensure your website stays online.",
  },
  {
    question: "Do you provide daily backups?",
    answer: "Yes, we perform automated daily backups of all hosting accounts. Backups are stored on separate servers for added security. You can also create manual backups anytime through cPanel or request restoration from our support team.",
  },
  {
    question: "How fast is your customer support?",
    answer: "Our customer support team is available 24/7 via live chat, phone, and ticket system. Average response time for live chat is under 2 minutes, and most tickets are resolved within 1 hour. Our support team consists of certified hosting experts.",
  },
];

const DedicatedServer = () => {
  return (
    <Layout>
       <SEOHead
         title="BDIX Dedicated Server Bangladesh | Xeon E5-2680v4 &amp; E5-2695v4 Servers"
         description="BDIX dedicated server in Bangladesh with Intel Xeon E5-2680v4 &amp; E5-2695v4 processors, NVMe RAID &amp; 10Gbps network. Dedicated server hosting Bangladesh from ৳26,499/month. Best dedicated servers in Bangladesh with 99.99% SLA."
         canonical="/bdix-dedicated-server"
         type="product"
         keywords="BDIX dedicated server, dedicated server Bangladesh, Xeon E5-2680v4 dedicated server, Xeon E5-2695v4 dedicated server, best dedicated server Bangladesh, reliable dedicated server, fastest dedicated server, server hosting Bangladesh, xeon dedicated server, dedicated servers in bangladesh, dedicated server hosting bangladesh, bdix hosting server, xeon e5-2680v4 dedicated server, xeon e5-2695v4 dedicated server"
       />
      <SchemaMarkup
        type="product"
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "BDIX Dedicated Server", url: "/bdix-dedicated-server" },
        ]}
        faqs={dedicatedFaqs}
        products={packages.map((p) => ({
          name: `${p.name} BDIX Dedicated Server Bangladesh`,
          description: `Dedicated server Bangladesh - ${p.processor}, ${p.ram} RAM, ${p.disk} NVMe`,
          price: p.price.replace(",", ""),
        }))}
        pageName="BDIX Dedicated Server Bangladesh - Dedicated Server BD"
        pageDescription="BDIX dedicated server, dedicated server Bangladesh. Best hosting provider in Bangladesh for enterprise from ৳26,499/month."
      />
      <PageHero
        badge="BDIX Dedicated Server"
        title="BDIX Dedicated Server Bangladesh"
        subtitle="Bare-metal performance with direct BDIX connectivity. Enterprise-grade Intel Xeon and 10Gbps network."
      />

      {/* Features Strip */}
      <section className="py-12 bg-muted/30 border-b border-border" aria-label="BDIX Dedicated Server Features">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {features.map((feature) => (
              <div
                key={feature.title}
                className="flex items-center gap-3"
              >
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0" aria-hidden="true">
                  <feature.icon className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <div className="font-medium text-foreground text-sm">{feature.title}</div>
                  <div className="text-xs text-muted-foreground">{feature.description}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ServerPricingTable
        packages={packages}
        title="Choose Your BDIX Dedicated Server"
        subtitle="Enterprise-grade BDIX dedicated servers with 24/7 hardware support. Best dedicated server price in Bangladesh."
      />

      <FAQSection />
      <PartnersSection />
    </Layout>
  );
};

export default DedicatedServer;
